import React, { useState, type FC, useEffect, createRef, useRef, type PropsWithChildren } from 'react';

import { languages, sendBeaconEvent, sendGoogleEvent, formatShortLangPrefix, getLanguage } from '@lucid/utils';
import styled from '@emotion/styled';

import Link from '../link/link';
import Icon from '../icon/icon';
import Button from '../button/button';
import type { CustomDrupal, I18n, Lucid } from '../../types/types';

export interface LanguageSwitcherProps {
  /** The aria label for screen readers */
  buttonAriaLabel: string;
  /** The aria label for screen readers for the nav tag */
  navAriaLabel?: string;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Hides the languages in the select option. */
  disabledLanguages?: I18n.AvailableLanguages[];
  /** The entity values to allow for customization of urls. */
  entityTranslations: Partial<CustomDrupal.ExtendedDrupalNode>[];
  /** The direction for the languages menu to open from. */
  popupDirection?: 'top' | 'bottom';
  /** The short language prefix to create proper urls. */
  shortLangPrefix: I18n.AvailableLanguages;
}
export interface LanguageOption {
  /** The english name for each language. */
  name: string;
  /** The language prefix. */
  shortLangPrefix: I18n.AvailableLanguages;
  /** The url for the current entity in each language */
  value: string;
  /** The native spelling for each language */
  label: string;
}

const LanguagesPopup = styled.ul<{
  isOpen: boolean;
  popupDirection: 'top' | 'bottom';
}>(({ theme, isOpen, popupDirection }) => [
  {
    display: isOpen ? 'block' : 'none',
    position: 'absolute',
    listStyle: 'none',
    margin: 0,
    backgroundColor: theme.colors.coolGrey[5],
    width: 'auto',
    padding: '0.5rem 0',
    left: '-3px',
    zIndex: theme.zIndices.z10,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: theme.colors.coolGrey[20],
    '&::before': {
      // keeps onMouseLeave event from triggering between button and menu
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
    },
    '&::after': {
      // popup menu corner
      left: '10px',
      content: '""',
      height: '20px',
      width: '20px',
      position: 'absolute',
      pointerEvents: 'none',
      backgroundColor: theme.colors.coolGrey[5],
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: theme.colors.coolGrey[20],
      borderRightWidth: '1px',
      borderRightStyle: 'solid',
      borderRightColor: theme.colors.coolGrey[20],
      zIndex: theme.zIndices.zNegative1,
    },
  },
  popupDirection === 'top' && {
    top: 'auto',
    bottom: 'calc(100% + 20px)',
    '&::before': {
      bottom: '-20px',
      right: '-20px',
    },
    '&::after': {
      // popup menu corner
      bottom: '-11px',
      transform: 'rotate(45deg)',
    },
  },
  popupDirection === 'bottom' && {
    top: 'calc(100% + 20px)',
    bottom: 'auto',
    '&::before': {
      bottom: '20px',
      right: '20px',
    },
    '&::after': {
      // popup menu corner
      top: '-11px',
      transform: 'rotate(225deg)',
    },
  },
]);

const LanguageSwitcher: FC<PropsWithChildren<LanguageSwitcherProps>> = ({
  buttonAriaLabel,
  navAriaLabel,
  className,
  disabledLanguages,
  entityTranslations,
  popupDirection = 'top',
  shortLangPrefix,
  ...rest
}) => {
  const currentShortLangPrefix = formatShortLangPrefix(shortLangPrefix);
  const shouldShowLangSelect = !disabledLanguages?.includes(currentShortLangPrefix);

  const languageOptions = (Object.keys(languages) as Array<keyof typeof languages>).reduce<LanguageOption[]>(
    (previous, currentLanguageKey) => {
      const { shortLangPrefix: sLangPrefixValue } = getLanguage(currentLanguageKey);
      const sLangPrefix = formatShortLangPrefix(sLangPrefixValue);

      if (disabledLanguages?.includes(sLangPrefix)) {
        return previous;
      }

      const { entityUrl } =
        (entityTranslations || []).find(
          ({ status, entityLanguage }) => status && entityLanguage!.id.includes(sLangPrefix),
        ) || {};

      if (entityUrl) {
        // check for drupal /homepage alias and remove it in the language switcher
        switch (entityUrl.path) {
          case `/${sLangPrefix}/`:
            entityUrl.path = `/${sLangPrefix}`;
            break;
          case '':
            entityUrl.path = '/';
            break;
        }
      }

      return [
        ...previous,
        {
          value: entityUrl?.path ? entityUrl.path : `/${sLangPrefix === 'en' ? '' : sLangPrefix}`,
          label: languages[currentLanguageKey].name,
          name: languages[currentLanguageKey].englishName,
          shortLangPrefix: sLangPrefix,
        },
      ];
    },
    [],
  );

  const [defaultValue] = languageOptions.filter(option => option.shortLangPrefix === currentShortLangPrefix);
  const currentLanguage = defaultValue?.name || 'English';
  const currentLangValue = defaultValue?.value || '/';
  const currentLangLabel = defaultValue?.label || 'English';
  const [isOpen, setIsOpen] = useState(false);
  const [optionInFocus, setOptionInFocus] = useState(0);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const langLinkRefs = useRef(languageOptions.map(() => createRef<HTMLLIElement>()));

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (['ArrowDown', 'Enter', ' '].includes(event.key)) {
      setIsOpen(isOpen);
    }
  };

  const handleLinkKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (['ArrowLeft', 'ArrowRight', 'Escape'].includes(event.key)) {
      setIsOpen(false);
      menuToggleRef?.current?.focus();
    }
    if (event.key === 'Tab') {
      setIsOpen(false);
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (optionInFocus >= languageOptions.length - 1) {
        // wrap to top
        return setOptionInFocus(0);
      }
      setOptionInFocus(optionInFocus + 1);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (optionInFocus === 0) {
        // wrap to bottom
        return setOptionInFocus(languageOptions.length - 1);
      }
      setOptionInFocus(optionInFocus - 1);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setOptionInFocus(0);
      sendBeaconEvent({
        name: 'clickedMarketingPageElement',
        elementLocation: popupDirection === 'bottom' ? 'nav' : 'footer',
        elementDetail: 'lang-toggle',
        elementType: 'link',
      });
    }
  }, [isOpen, popupDirection]);

  useEffect(() => {
    if (isOpen) {
      langLinkRefs?.current[optionInFocus]?.current?.querySelector('a')?.focus();
    }
  }, [optionInFocus, isOpen]);

  return shouldShowLangSelect ? (
    <nav
      {...rest}
      aria-label={navAriaLabel}
      css={(theme: Lucid.Theme) => ({
        position: 'relative',
        marginRight: theme.space.s32,
      })}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
    >
      <Button
        ref={menuToggleRef}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={buttonAriaLabel}
        css={(theme: Lucid.Theme) => ({
          fontWeight: theme.fontWeights.regular,
          display: 'flex',
          alignItems: 'center',
          gap: theme.space.s8,
        })}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleMenuKeyDown}
        role="button"
        tabIndex={0}
        variant="languageSwitcher"
      >
        <Icon
          color="coolGrey.90"
          css={{
            width: '20px',
            height: '20px',
          }}
          type="Globe"
        />
        {currentLangLabel}
        <Icon
          color="coolGrey.90"
          css={{
            transition: 'transform 0.3s',
            transformOrigin: 'center',
            transform: isOpen ? 'scaleY(-1)' : 'scaleY(1)',
            width: '10px',
          }}
          type="CarotDown"
        />
      </Button>
      <LanguagesPopup
        data-testid="languageSwitcherMenu"
        isOpen={isOpen}
        onMouseLeave={() => setIsOpen(false)}
        popupDirection={popupDirection}
        role="menu"
      >
        {languageOptions?.map(({ shortLangPrefix, value, label, name }, i) => (
          <li key={`lang-${shortLangPrefix}`} ref={langLinkRefs.current[i]}>
            <Link
              css={(theme: Lucid.Theme) => [
                {
                  position: 'relative',
                  width: '100%',
                  padding: `${theme.space.s4}px ${theme.space.s24}px`,
                  fontWeight: theme.fontWeights.regular,
                  borderColor: 'transparent',
                  borderStyle: 'solid',
                  borderWidth: '2px',
                  borderRadius: theme.radii.textOnly,
                  '&:focus::before': {
                    border: 'none',
                  },
                  '&:focus:not(:focus-visible)': {
                    color: theme.colors.coolGrey[90],
                  },
                  '&:hover, &:focus-visible': {
                    color: theme.colors.coolGrey[90],
                    borderColor: theme.colors.primary,
                  },
                  '&:focus-visible': {
                    color: theme.colors.coolGrey[90],
                    outlineColor: theme.colors.primary,
                    outlineWidth: '2px',
                  },
                },
                value === currentLangValue && {
                  '&::after': {
                    content: '"✓" / ""',
                    marginLeft: '5px',
                    color: theme.colors.primary,
                  },
                },
              ]}
              lang={shortLangPrefix}
              onClick={() => {
                sendGoogleEvent({
                  name: 'changedPageLanguage',
                  data: {
                    langBefore: currentLanguage,
                    langAfter: name,
                  },
                });
              }}
              onKeyDown={handleLinkKeyDown}
              role="menuitem"
              tabIndex={-1}
              to={value}
              variant="subNavLink"
            >
              {label}
            </Link>
          </li>
        ))}
      </LanguagesPopup>
    </nav>
  ) : null;
};

export { LanguageSwitcher };
export default LanguageSwitcher;
